import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mail, X, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Visitor-side email capture. Two experiences, never both: an exit-intent dialog on pointer
// devices, a small sticky tab on touch devices. Nothing shows until the visitor has engaged, and a
// dismissal is remembered for 30 days. No third-party script, no animation library.

const DISMISS_DAYS = 30;
const DISMISS_MS = DISMISS_DAYS * 24 * 60 * 60 * 1000;
const ENGAGE_MS = 25000;
const SCROLL_RATIO = 0.5;

const KEY_DISMISSED = "hhl_email_dismissed_at";
const KEY_SUBSCRIBED = "hhl_email_subscribed";
const KEY_VIEWS = "hhl_session_page_views";

// Pages already counted in this mount, so StrictMode's repeated effects count a page once.
const countedPaths = new Set();

const sessionViews = () => {
  try {
    return Number(window.sessionStorage.getItem(KEY_VIEWS) || 0);
  } catch {
    return 0;
  }
};

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode: the prompt simply shows again next visit */
  }
};

export const markEmailSignupComplete = () => write(KEY_SUBSCRIBED, "1");

const suppressed = () => {
  if (read(KEY_SUBSCRIBED) === "1") return true;
  const at = Number(read(KEY_DISMISSED) || 0);
  return at > 0 && Date.now() - at < DISMISS_MS;
};

const isTouch = () =>
  window.matchMedia("(max-width: 1023px)").matches ||
  window.matchMedia("(pointer: coarse)").matches;

const SignupFields = ({ source, onDone, dark = false, idPrefix }) => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const startedAt = useRef(Date.now());

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Please tick the box so we may email you.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/subscribe", {
        email,
        consent: true,
        company,
        source,
        tags: ["General Newsletter"],
        elapsed_ms: Date.now() - startedAt.current,
      });
      markEmailSignupComplete();
      onDone(data);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "That didn't go through. Please check the address and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const muted = dark ? "text-[#CBC8C2]" : "text-[#6A665F]";
  const field = dark
    ? "bg-transparent border-white/25 placeholder:text-[#9E9B95] text-white focus:border-sage"
    : "bg-white border-line placeholder:text-[#9E9B95] text-charcoal focus:border-sage";

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} noValidate>
      <label htmlFor={`${idPrefix}-email`} className="sr-only">
        Email address
      </label>
      <input
        id={`${idPrefix}-email`}
        name="email"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email address"
        data-testid={`${idPrefix}-email`}
        className={`w-full border px-4 py-3.5 text-[15px] font-light focus:outline-none transition-colors ${field}`}
      />

      {/* Honeypot: hidden from people, irresistible to bots. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
        <label htmlFor={`${idPrefix}-company`}>Company</label>
        <input
          id={`${idPrefix}-company`}
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>

      <label
        htmlFor={`${idPrefix}-consent`}
        className={`flex items-start gap-2.5 mt-4 text-[12.5px] font-light leading-relaxed cursor-pointer ${muted}`}
      >
        <input
          id={`${idPrefix}-consent`}
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid={`${idPrefix}-consent`}
          className="mt-[3px] h-4 w-4 shrink-0 accent-[#7a8b73]"
        />
        <span>
          Yes, email me occasional buying guides, product discoveries and home living inspiration.
        </span>
      </label>

      {error && (
        <p data-testid={`${idPrefix}-error`} className="text-[12.5px] text-[#991B1B] mt-3">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid={`${idPrefix}-submit`}
        className="w-full mt-4 px-6 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-sage-dark transition-colors duration-300 disabled:opacity-60 min-h-[46px]"
      >
        {busy ? "Joining…" : "Join Our Email List"}
      </button>
    </form>
  );
};

const Done = ({ data, dark = false, idPrefix }) => (
  <div data-testid={`${idPrefix}-success`} className="text-center py-2">
    <Check className="text-sage mx-auto" size={26} />
    <h3 className={`display text-[24px] mt-3 ${dark ? "text-white" : "text-charcoal"}`}>
      {data?.already_subscribed ? "You're already on the list." : "Thank you for joining."}
    </h3>
    <p className={`text-[14px] font-light mt-2 ${dark ? "text-[#CBC8C2]" : "text-[#6A665F]"}`}>
      {data?.already_subscribed
        ? "This address is already subscribed, so nothing further is needed."
        : data?.confirmation_sent
        ? "Please check your inbox and confirm your subscription."
        : "Your details are saved. We'll confirm your subscription by email shortly."}
    </p>
  </div>
);

const ExitIntentDialog = ({ onClose }) => {
  const [result, setResult] = useState(null);
  const ref = useRef(null);
  const restoreTo = useRef(null);

  useEffect(() => {
    restoreTo.current = document.activeElement;
    ref.current?.querySelector("input[type=email]")?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key !== "Tab") return;
      const nodes = ref.current?.querySelectorAll(
        'button, input:not([tabindex="-1"]), a[href]'
      );
      if (!nodes?.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      restoreTo.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center px-5 bg-charcoal/55"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="email-exit-overlay"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-exit-heading"
        data-testid="email-exit-popup"
        className="relative w-full max-w-[520px] bg-bone border border-line shadow-[0_24px_60px_rgba(51,51,51,0.22)]"
      >
        <button
          onClick={onClose}
          aria-label="Close email signup"
          data-testid="email-exit-close"
          className="absolute right-3 top-3 h-10 w-10 flex items-center justify-center text-[#6A665F] hover:text-charcoal transition-colors"
        >
          <X size={18} />
        </button>

        <div className="px-8 sm:px-11 py-11">
          {result ? (
            <Done data={result} idPrefix="email-exit" />
          ) : (
            <>
              <span className="eyebrow">Human Home Living</span>
              <h2
                id="email-exit-heading"
                className="display text-[34px] sm:text-[40px] leading-[1.08] text-charcoal mt-4"
              >
                Make More of Home.
              </h2>
              <p className="text-[15.5px] font-light leading-[1.7] text-[#6A665F] mt-4 mb-7">
                Get useful buying guides, product discoveries, and home living inspiration
                delivered to your inbox.
              </p>
              <SignupFields
                source="desktop-exit-intent"
                idPrefix="email-exit"
                onDone={setResult}
              />
              <p className="text-[12px] font-light text-[#8A867F] mt-4">
                Occasional emails. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StickyTab = ({ onDismiss }) => {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);

  if (!open)
    return (
      <div className="fixed right-0 top-[62%] -translate-y-1/2 z-[70]">
        <button
          onClick={() => setOpen(true)}
          data-testid="email-sticky-tab"
          aria-label="Join our email list"
          className="flex flex-col items-center gap-2 bg-sage text-white py-4 px-2.5 min-w-[44px] min-h-[132px] rounded-l-sm shadow-[0_6px_18px_rgba(51,51,51,0.18)]"
        >
          <Mail size={15} />
          <span
            className="text-[10.5px] tracking-[0.16em] uppercase leading-none"
            style={{ writingMode: "vertical-rl" }}
          >
            Join Our Email List
          </span>
        </button>
      </div>
    );

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[80] bg-bone border border-line shadow-[0_16px_40px_rgba(51,51,51,0.25)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      role="dialog"
      aria-label="Join our email list"
      data-testid="email-sticky-panel"
    >
      <button
        onClick={() => {
          setOpen(false);
          onDismiss();
        }}
        aria-label="Dismiss email signup"
        data-testid="email-sticky-close"
        className="absolute right-1.5 top-1.5 h-11 w-11 flex items-center justify-center text-[#6A665F]"
      >
        <X size={18} />
      </button>
      <div className="px-5 pt-6 pb-5">
        {result ? (
          <Done data={result} idPrefix="email-sticky" />
        ) : (
          <>
            <h2 className="display text-[25px] leading-[1.15] text-charcoal pr-10">
              Get More from Home Living
            </h2>
            <p className="text-[13.5px] font-light leading-[1.6] text-[#6A665F] mt-2.5 mb-5">
              Useful buying guides, product discoveries, and inspiration. Delivered occasionally.
            </p>
            <SignupFields source="mobile-sticky-tab" idPrefix="email-sticky" onDone={setResult} />
            <p className="text-[12px] font-light text-[#8A867F] mt-3">Unsubscribe anytime.</p>
          </>
        )}
      </div>
    </div>
  );
};

export const EmailSignupManager = () => {
  const { pathname } = useLocation();
  const [mode, setMode] = useState(null); // null | "tab" | "popup"
  const [engaged, setEngaged] = useState(false);

  const dismiss = useCallback(() => {
    write(KEY_DISMISSED, String(Date.now()));
    setMode(null);
  }, []);

  // Engagement gate: time on site, meaningful scroll, or a second page in this session. Each path
  // is counted once, so StrictMode's double effect run cannot inflate the count.
  useEffect(() => {
    if (suppressed()) {
      // e.g. the visitor just signed up through the homepage block: drop any tab/popup already armed.
      setMode(null);
      setEngaged(false);
      return undefined;
    }
    if (!countedPaths.has(pathname)) {
      countedPaths.add(pathname);
      try {
        const views = sessionViews() + 1;
        window.sessionStorage.setItem(KEY_VIEWS, String(views));
      } catch {
        /* ignore */
      }
    }
    if (countedPaths.size > 1 || sessionViews() > 1) {
      setEngaged(true);
      // A link click leaves the anchor focused, which would otherwise block the exit-intent check.
      if (document.activeElement?.tagName === "A") document.activeElement.blur();
      return undefined;
    }
    const timer = setTimeout(() => setEngaged(true), ENGAGE_MS);
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > SCROLL_RATIO) setEngaged(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  useEffect(() => {
    if (!engaged || suppressed()) return undefined;
    if (isTouch()) {
      setMode("tab");
      return undefined;
    }
    const onMouseOut = (e) => {
      if (e.relatedTarget || e.clientY > 8 || suppressed()) return;
      const active = document.activeElement;
      // Never interrupt someone mid-form, and never while a link click is in flight.
      if (active && ["INPUT", "TEXTAREA", "SELECT", "A"].includes(active.tagName)) return;
      setMode("popup");
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [engaged]);

  if (mode === "popup") return <ExitIntentDialog onClose={dismiss} />;
  if (mode === "tab") return <StickyTab onDismiss={dismiss} />;
  return null;
};
